// /rf-admin/admins — admin 管理 (super only)
// UI 复刻 /dashboard:Card + PageTitle + Table + Badge
// M5 統一 CRUD: 編集 / リセットPW / 停止・有効化
// Modal 复刻 globals.css 里 blog-edit-modal-* 的 style(SaaS Blog modal 也用同一份)。

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  rfAdminListAdmins,
  rfAdminCreateAdmin,
  rfAdminEditAdmin,
  rfAdminResetAdminPassword,
} from "../api/admin";
import type { RfAdminUser } from "../api/client";
import { Card } from "../../components/ui/Card";
import { Table, type TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { PageTitle } from "../../components/ui/PageTitle";
import { Button } from "../../components/ui/Button";

const STATUS_COLOR: Record<string, "green" | "gray" | "yellow" | "red"> = {
  active: "green",
  disabled: "gray",
  invited: "yellow",
};
const STATUS_LABEL: Record<string, string> = {
  active: "稼働中",
  disabled: "停止中",
  invited: "招待中",
};

// ─────────────────── Edit Admin Dialog ───────────────────

function RfEditAdminDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: RfAdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(admin.email);
  const [role, setRole] = useState<"super" | "sub">(admin.role);
  const [status, setStatus] = useState<"active" | "disabled">(admin.status);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      const patch: { email?: string; role?: "super" | "sub"; status?: "active" | "disabled" } = {};
      if (email !== admin.email) patch.email = email;
      if (role !== admin.role) patch.role = role;
      if (status !== admin.status) patch.status = status;
      await rfAdminEditAdmin(admin.id, patch);
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="blog-edit-modal-overlay" role="dialog" aria-modal="true">
      <div className="blog-edit-modal-container" style={{ maxWidth: 520 }}>
        <div className="blog-edit-modal-header">
          <div className="blog-edit-modal-title">Admin 編集</div>
          <button type="button" className="blog-edit-modal-close" onClick={onClose} aria-label="閉じる">
            <X size={16} />
          </button>
        </div>
        <div className="blog-edit-modal-body">
          <div className="rf-admin-form">
            <div className="field">
              <label className="field-label">Email <span className="field-label-required">*</span></label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label className="field-label">Role</label>
              <select
                className="select"
                value={role}
                onChange={(e) => setRole(e.target.value as "super" | "sub")}
              >
                <option value="sub">sub (サブ)</option>
                <option value="super">super (スーパー)</option>
              </select>
              <div className="field-hint">最後の super を sub に降格、または停止しようとするとエラーになります。</div>
            </div>
            <div className="field">
              <label className="field-label">状態</label>
              <select
                className="select"
                value={status}
                onChange={(e) => setStatus(e.target.value as "active" | "disabled")}
              >
                <option value="active">active (稼働中)</option>
                <option value="disabled">disabled (停止中)</option>
              </select>
            </div>
            {error && <div className="login-error">{error}</div>}
            <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
              <Button variant="ghost" onClick={onClose}>
                キャンセル
              </Button>
              <Button variant="primary" onClick={onSave} disabled={saving}>
                {saving ? "保存中..." : "保存する"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Main Page ───────────────────

export function RfAdminAdmins() {
  const [admins, setAdmins] = useState<RfAdminUser[] | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"super" | "sub">("sub");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editAdmin, setEditAdmin] = useState<RfAdminUser | null>(null);

  function reload() {
    rfAdminListAdmins()
      .then(setAdmins)
      .catch((e) => setError(e.message));
  }

  useEffect(reload, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await rfAdminCreateAdmin({ email, password, role });
      setEmail("");
      setPassword("");
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const columns: TableColumn<RfAdminUser>[] = [
    { key: "id", header: "id", render: (a) => a.id },
    { key: "email", header: "email", render: (a) => a.email },
    {
      key: "role",
      header: "role",
      render: (a) => (
        <Badge color={a.role === "super" ? "purple" : "gray"}>
          {a.role === "super" ? "Super" : "Sub"}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "状態",
      render: (a) => (
        <Badge color={STATUS_COLOR[a.status] ?? "gray"} dot>
          {STATUS_LABEL[a.status] ?? a.status}
        </Badge>
      ),
    },
    {
      key: "last_login",
      header: "最終ログイン",
      render: (a) => a.last_login_at ?? "—",
    },
    {
      key: "actions",
      header: "操作",
      align: "right",
      render: (a) => (
        <div className="table-actions" style={{ flexWrap: "wrap", gap: 4 }}>
          <Button variant="ghost" onClick={() => setEditAdmin(a)}>
            編集
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              const pw = window.prompt(
                `${a.email} の新しいパスワードを入力してください(12文字以上・大小英字・数字・記号)`,
              );
              if (!pw) return;
              try {
                await rfAdminResetAdminPassword(a.id, pw);
                alert("パスワードをリセットしました");
              } catch (e) {
                alert(`リセット失敗: ${(e as Error).message}`);
              }
            }}
          >
            リセットPW
          </Button>
          {a.status === "active" ? (
            <Button
              variant="danger"
              onClick={async () => {
                if (!confirm(`${a.email} を停止しますか?`)) return;
                try {
                  await rfAdminEditAdmin(a.id, { status: "disabled" });
                  reload();
                } catch (e) {
                  alert(`停止失敗: ${(e as Error).message}`);
                }
              }}
            >
              停止
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  await rfAdminEditAdmin(a.id, { status: "active" });
                  reload();
                } catch (e) {
                  alert(`有効化失敗: ${(e as Error).message}`);
                }
              }}
            >
              有効化
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="rf-admin-page">
      <PageTitle title="Admins" subtitle="RF Admin Console の管理者ユーザー" />

      <Card title="新規作成" subtitle="Email と Role を指定。Password は Email の下に表示されます。">
        {error && <div className="login-error" style={{ marginBottom: 12 }}>{error}</div>}
        <form onSubmit={onCreate} className="rf-admin-form">
          {/* Email — 1 行目 */}
          <div className="field" style={{ marginBottom: 12 }}>
            <label htmlFor="admin-email" className="field-label">
              Email <span className="field-label-required">*</span>
            </label>
            <input
              id="admin-email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password — Email の下 */}
          <div className="field" style={{ marginBottom: 12 }}>
            <label htmlFor="admin-pw" className="field-label">
              Password <span className="field-label-required">*</span>
            </label>
            <input
              id="admin-pw"
              type="text"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
            />
            <div className="field-hint">12文字以上、大小英字・数字・記号を含む</div>
          </div>

          {/* Role + ボタン — Email/Password の下、行に並べる */}
          <div style={{ display: "flex", gap: 12, alignItems: "end" }}>
            <div className="field" style={{ marginBottom: 0, flex: 1, maxWidth: 220 }}>
              <label htmlFor="admin-role" className="field-label">
                Role
              </label>
              <select
                id="admin-role"
                className="select"
                value={role}
                onChange={(e) => setRole(e.target.value as "super" | "sub")}
              >
                <option value="sub">sub</option>
                <option value="super">super</option>
              </select>
            </div>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "作成中..." : "作成する"}
            </Button>
          </div>
        </form>
      </Card>

      <div className="mb-18" />

      <Card title="既存 admin" subtitle={`${admins?.length ?? 0} 件`}>
        {admins === null ? (
          <p>読み込み中...</p>
        ) : (
          <Table<RfAdminUser>
            columns={columns}
            data={admins}
            rowKey={(a) => String(a.id)}
            emptyText="まだ admin がいません。"
          />
        )}
      </Card>

      {editAdmin && (
        <RfEditAdminDialog
          admin={editAdmin}
          onClose={() => setEditAdmin(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
